import { inject, Injectable } from '@angular/core'

import { BehaviorSubject, takeUntil } from 'rxjs'

import { Memoize } from '@ti/core/common/decorators'
import { WhiteLabelSettingsProvider } from '@ti/core/common/services'
import { exposeReadonlyState, toSignalState } from '@ti/core/common/utils'
import { ViewModel } from '@ti/core/models/view.model'

import { Shade, ThemeName } from '../contracts'
import { simpleGeneratePalette } from './theme-generator'

export const PRE_DEFIED_COLOR_SCHEMES: Record<ThemeName, string> = {
  purple: '#8B5CF6',
  darkBlue: '#5766C7',
  mint: '#33AD7C',
  burgundy: '#970012',
  orange: '#F8772C',
  lightblue: '#428FE0',
}

@Injectable({ providedIn: 'root' })
export class ThemesManager extends ViewModel {
  protected readonly whiteLabelSettings = inject(WhiteLabelSettingsProvider)
  protected readonly themesPaletteCache = new Map<ThemeName | string, Record<number, string>>()

  protected readonly store$ = {
    current: new BehaviorSubject<ThemeName>('purple'),
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      ...exposeReadonlyState(this.store$, ['current']),
    }
  }

  public apply(theme: ThemeName) {
    const palette = this.getThemePalette(theme)
    if (!palette) return

    const root = document.documentElement
    const shades: Shade[] = [700, 600, 500, 400, 300, 200, 100, 50, 25]
    for (const shade of shades) {
      if (theme !== 'purple') {
        const value = palette[shade]
        if (value) {
          root.style.setProperty(`--color-primary-${shade}`, value)
        }
      } else {
        root.style.removeProperty(`--color-primary-${shade}`)
      }
    }

    this.store$.current.next(theme)
  }

  public getThemePalette(theme: ThemeName | string) {
    if (!this.themesPaletteCache.has(theme)) {
      const color = theme.includes('#') ? theme : PRE_DEFIED_COLOR_SCHEMES[theme as ThemeName]
      this.themesPaletteCache.set(theme, simpleGeneratePalette(color))
    }
    return this.themesPaletteCache.get(theme)!
  }

  protected override init() {
    this.whiteLabelSettings.data$.pipe(takeUntil(this.destroy$)).subscribe((settings) => {
      if (settings?.colorScheme) {
        this.apply(settings.colorScheme as ThemeName)
      }
    })
  }
}
